// Shared cluster-bubble anchoring. MapLibre/supercluster place a cluster's
// geometry at the *centroid* of its points. In Norway's fjord/archipelago
// geography that centroid routinely falls on land, mid-fjord, or — averaging
// Barents-coast points — well inland (Finland/Sweden). So every HTML cluster
// bubble looks misplaced.
//
// Instead we anchor each bubble to a real member position (always a valid
// location): getClusterLeaves(id, 1) gives the first leaf's coordinate, cached
// per (source, cluster id). Until it resolves we fall back to the centroid and
// trigger a repaint so the next sync snaps it into place.

import maplibregl from "maplibre-gl";

const cache = new Map<string, [number, number]>();
const pending = new Set<string>();

/**
 * Representative coordinate for a cluster — a real member position, not the
 * centroid. Returns the centroid fallback until the leaf lookup resolves.
 */
export function anchorCoord(
  map: maplibregl.Map,
  source: string,
  clusterId: number,
  fallback: [number, number],
): [number, number] {
  const key = `${source}:${clusterId}`;
  const hit = cache.get(key);
  if (hit) return hit;
  if (!pending.has(key)) {
    pending.add(key);
    const src = map.getSource(source) as maplibregl.GeoJSONSource | undefined;
    src
      ?.getClusterLeaves(clusterId, 1, 0)
      .then((leaves) => {
        const g = leaves[0]?.geometry as GeoJSON.Point | undefined;
        if (g) cache.set(key, g.coordinates as [number, number]);
        pending.delete(key);
        map.triggerRepaint(); // re-render so the next sync repositions the bubble
      })
      .catch(() => pending.delete(key));
  }
  return fallback;
}

/** Drop cached anchors for a source (call when its markers are reset). */
export function clearAnchorCache(source: string): void {
  for (const k of [...cache.keys()]) if (k.startsWith(`${source}:`)) cache.delete(k);
  for (const k of [...pending]) if (k.startsWith(`${source}:`)) pending.delete(k);
}
